import React, {
    useRef,
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle
} from "react";
import ReactECharts from "echarts-for-react";
import {
    Box,
    CircularProgress,
    IconButton,
    MenuItem,
    Popover,
    TextField,
    Tooltip,
    Typography, useTheme
} from "@mui/material";
import { msg, Trans } from "@lingui/macro";
import { Download, LockOutlined, PictureAsPdf } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useLingui } from "@lingui/react";
import useWindowSize from "../../../hooks/useWindowSize";
import jsPDF from "jspdf";
import SvgCustomIcon from "../../icons/SvgCustomIcon";
import {
    statisticsBarFillColor,
    statisticsMapGradientColors,
    statisticsTreemapColors
} from "./statistics-chart-palette";
import { buildVerticalCategoryAxisOptions, captureEchartsPng } from "./chart-category-axis-layout";

const EChart = forwardRef(({ areasGeoJson, chart, index, handleCharts, width, height }, ref) => {
    const windowSize = useWindowSize();
    const { _ } = useLingui();

    const [anchorEl, setAnchorEl] = useState(null);
    const [locationsList, setLocationsList] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');
    const theme = useTheme();

    const chartRef = useRef();

    // Espone metodi al parent
    useImperativeHandle(ref, () => ({
        getImage: async () => {
            const instance = chartRef.current?.getEchartsInstance();
            return captureEchartsPng(instance, {
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#fff",
            });
        },
    }));

    useEffect(() => {
        if (chart.type === "map") {
            const l = chart.data
                .filter(el => el.name)
                .map(el => ({
                    geoName: el.name === "Chhuk" ? "Chuuk" : el.name,
                    displayLabel: el.label ?? el.name,
                }));
            setLocationsList(l);
            setSelectedLocation(l[0]?.geoName ?? '');
        }
    }, [chart]);

    useEffect(() => {
        if (selectedLocation) {
            centerOnRegion(selectedLocation);
        }
    }, [selectedLocation]);

    const getRegionCenterByName = regionName => {
        const regionNameRefactor = regionName === "Chhuk" ? "Chuuk" : regionName;
        const feature = areasGeoJson?.features.find(
            f => f.properties.Name === regionNameRefactor
        );
        if (!feature) return null;
        return getFeatureCenter(feature);
    };

    const getFeatureCenter = feature => {
        const coordinates = feature.geometry.coordinates;
        const polygon =
            feature.geometry.type === "MultiPolygon"
                ? coordinates[0][0]
                : coordinates[0];
        let x = 0,
            y = 0,
            len = polygon.length;
        polygon.forEach(coord => {
            x += coord[0];
            y += coord[1];
        });
        return [x / len, y / len];
    };

    const centerOnRegion = regionName => {
        const center = getRegionCenterByName(regionName);
        if (!center) return;
        const instance = chartRef.current?.getEchartsInstance();
        instance?.setOption({
            series: [{ center, zoom: 12, roam: true }]
        });
    };

    const getChartOption = (chart) => {
        const labels = chart.data.map(el => el.label);
        const values = chart.data.map(el => el.value);

        const isDark = theme.palette.mode === "dark";
        const textColor = isDark ? "#fff" : "#333";
        const gridColor = isDark ? "#888" : "#ccc";

        const resolveOrdinalColors = (c) =>
            c.colorMap?.type === "ordinal" &&
            Array.isArray(c.colorMap?.colors) &&
            c.colorMap.colors.length > 0
                ? c.colorMap.colors
                : statisticsTreemapColors;

        switch (chart.type) {
            case "bar": {
                const barMono =
                    chart.colorMap?.colors?.[0] ?? statisticsBarFillColor;
                const { axisLabel, grid, dataZoom, tooltip } = buildVerticalCategoryAxisOptions(
                    labels,
                    textColor,
                );

                return {
                    color: [barMono],
                    tooltip,
                    grid,
                    ...(dataZoom ? { dataZoom } : {}),
                    xAxis: {
                        type: "category",
                        data: labels,
                        name: chart.xLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel,
                    },
                    yAxis: {
                        type: "value",
                        name: chart.yLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor },
                    },
                    series: [
                        {
                            type: "bar",
                            data: values.map((v) => ({
                                value: v,
                                itemStyle: {
                                    color: barMono,
                                },
                            })),
                            label: {
                                show: true,
                                position: "top",
                                color: textColor,
                                formatter: "{c}",
                            },
                        },
                    ],
                };
            }

            case "line": {
                const lineColors = resolveOrdinalColors(chart);
                const palette = lineColors.length > 0 ? lineColors : statisticsTreemapColors;
                const lineStroke = palette[0] ?? statisticsBarFillColor;
                return {
                    color: palette,
                    xAxis: {
                        type: "category",
                        data: labels,
                        name: chart.xLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: {
                            color: textColor,
                            interval: 0,
                            rotate: labels.length > 3 ? 35 : 0
                        }
                    },
                    yAxis: {
                        type: "value",
                        name: chart.yLabel,
                        axisLine: { lineStyle: { color: gridColor } },
                        axisLabel: { color: textColor }
                    },
                    series: [
                        {
                            type: "line",
                            data: values.map((v, i) => ({
                                value: v,
                                itemStyle: {
                                    color: palette[i % palette.length]
                                }
                            })),
                            label: {
                                show: true,
                                position: "top",
                                color: textColor
                            },
                            lineStyle: { width: 3, color: lineStroke }
                        }
                    ]
                };
            }

            case "pie": {
                const pieColors = resolveOrdinalColors(chart);
                const pieSeriesColors = chart.data.map(
                    (_, i) => pieColors[i % pieColors.length]
                );
                return {
                    color: pieSeriesColors,
                    tooltip: {
                        trigger: "item",
                        textStyle: { color: textColor },
                        formatter: params =>
                            `${params.name}: ${params.value.toFixed(1)} (${params.percent.toFixed(1)}%)`
                    },
                    series: [
                        {
                            type: "pie",
                            radius: "60%",
                            data: chart.data.map(el => ({
                                name: el.label,
                                value: el.value
                            })),
                            label: {
                                show: true,
                                color: textColor,
                                formatter: params =>
                                    `${params.name}\n${params.value.toFixed(0)} (${params.percent.toFixed(0)}%)`
                            },
                            labelLine: {
                                show: true,
                                lineStyle: { color: gridColor }
                            }
                        }
                    ]
                };
            }

            case "treemap": {
                const treemapColors = resolveOrdinalColors(chart)
                const n = Math.max(chart.data?.length ?? 0, 1)
                const seriesColorList = Array.from(
                    { length: n },
                    (_, i) => treemapColors[i % treemapColors.length]
                )
                return {
                    color: seriesColorList,
                    title: {
                        show: chart?.title?.show ?? true,
                        text: chart?.title?.text,
                        left: "center",
                        textStyle: { fontSize: 16, color: textColor }
                    },
                    tooltip: { formatter: "{b}: {c}", textStyle: { color: textColor } },
                    series: [
                        {
                            type: "treemap",
                            data: chart.data.map(el => ({
                                name: el.label,
                                value: el.value
                            })),
                            label: {
                                show: true,
                                color: '#1a1a1a',
                                fontWeight: 600,
                                textBorderColor: '#ffffff',
                                textBorderWidth: 3
                            },
                            upperLabel: {
                                show: true,
                                color: '#1a1a1a',
                                fontWeight: 600,
                                textBorderColor: '#ffffff',
                                textBorderWidth: 3
                            },
                            itemStyle: {
                                gapWidth: 2,
                                borderColor: "#ffffff",
                                borderWidth: 2,
                                color: (params) =>
                                    treemapColors[params.dataIndex % treemapColors.length]
                            }
                        }
                    ]
                }
            }

            case "map": {
                const mapValues =
                    chart.data
                        ?.map(el => el.value)
                        .filter(v => typeof v === "number" && !isNaN(v)) ?? [];
                const maxVal = mapValues.length > 0 ? Math.max(...mapValues) : 0;
                const mapVisualColors =
                    chart.colorMap?.type === 'visualMap' &&
                    Array.isArray(chart.colorMap?.inRange?.color)
                        ? chart.colorMap.inRange.color
                        : statisticsMapGradientColors;
                return {
                    title: {
                        text: chart?.title?.text,
                        show: chart?.title?.show ?? true,
                        left: "center",
                        textStyle: { fontSize: 16, color: textColor }
                    },
                    tooltip: {
                        trigger: "item",
                        formatter: (params) =>
                            `${params.name}: ${Number(params.value) || 0}`,
                        textStyle: { color: textColor }
                    },
                    visualMap: {
                        min: 0,
                        max: maxVal,
                        left: "left",
                        top: "bottom",
                        text: ["+", "-"],
                        textStyle: { color: textColor },
                        calculable: true,
                        inRange: {
                            color: mapVisualColors
                        }
                    },
                    series: [
                        {
                            name: chart.seriesName || "",
                            type: "map",
                            map: chart.mapType || "world",
                            roam: true,
                            zoom: 3,
                            nameProperty: "Name",
                            data:
                                chart.data?.map(el => ({
                                    name: el.name === "Chhuk" ? "Chuuk" : el.name,
                                    value: Number(el.value) || 0,
                                    label: {
                                        show: true,
                                        formatter: "{c}",
                                        color: textColor,
                                        fontSize: 16,
                                        fontWeight: "bold",
                                        textBorderColor: isDark ? "#000" : "#fff",
                                        textBorderWidth: 2
                                    },
                                    emphasis: {
                                        label: {
                                            show: true,
                                            color: "#fff",
                                            fontWeight: "bold"
                                        }
                                    }
                                })) ?? []
                        }
                    ]
                };
            }

            default:
                return {};
        }
    };



    const exportToPdf = async () => {
        const instance = chartRef.current?.getEchartsInstance();
        if (!instance) return;

        const imgData = await captureEchartsPng(instance, {
            type: "png",
            pixelRatio: 2,
            backgroundColor: "#fff",
        });
        if (!imgData) return;

        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const img = new Image();
        img.src = imgData;
        img.onload = () => {
            const imgWidth = img.width;
            const imgHeight = img.height;
            const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);

            const renderWidth = imgWidth * ratio;
            const renderHeight = imgHeight * ratio;

            const x = (pageWidth - renderWidth) / 2;
            const y = (pageHeight - renderHeight) / 2;

            pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
            pdf.save(`chart${chart?.title?.text ? '-' : ''}${chart?.title?.text || ""}.pdf`);
            setAnchorEl(null);
        };
    };

    const exportToExcel = () => {
        const data = chart.data;
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Grafico");
        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });
        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        saveAs(blob, `chart${chart?.title?.text ? '-' : ''}${chart?.title?.text || ""}-data.xlsx`);
        setAnchorEl(null);
    };



    const fillHeight = height === "100%"
    const chartHeight = fillHeight ? "100%" : (typeof height === "number" ? height : 500) + (chart?.margin?.bottom ?? 0)

    return (
        <Box
            width={width}
            display="flex"
            flexDirection="column"
            alignItems="center"
            p={width === "100%" ? 2 : 1}
            gap={2}
            sx={fillHeight ? { height: "100%", minHeight: 0 } : undefined}
        >
            {/* HEADER con titolo e controlli */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: `1px solid ${theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray}`,
                    pb: 1,
                }}
            >
                {/* Titolo */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: theme.palette.mode === 'light' ? theme.palette.newColors.gray : theme.palette.newColors.lightGray,
                    }}
                >
                    <Trans>{chart?.title?.text || ""}</Trans>
                </Typography>

                {/* Controlli a destra */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                        border: "1px solid #ddd",
                        borderRadius: 2,

                    }}
                >
                    {!chart.sensitiveField && chart.type !== "map" && (
                        <TextField
                            select
                            size="small"
                            value={chart.type}
                            onChange={(event) => handleCharts(event, chart.apiNum ?? index)}
                            sx={{
                                "& .MuiInputBase-root": {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                                    fontSize: 14,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            <MenuItem value="line">
                                <Trans>line</Trans>
                            </MenuItem>
                            <MenuItem value="bar">
                                <Trans>bar</Trans>
                            </MenuItem>
                            <MenuItem value="pie">
                                <Trans>pie</Trans>
                            </MenuItem>
                            <MenuItem value="treemap">
                                <Trans>treemap</Trans>
                            </MenuItem>
                        </TextField>
                    )}

                    {chart.type === "map" && !chart.sensitiveField && (
                        <TextField
                            select
                            size="small"
                            value={selectedLocation}
                            onChange={(event) => setSelectedLocation(event.target.value)}
                            sx={{
                                "& .MuiInputBase-root": {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'light' ? theme.palette.newColors.lightGray : theme.palette.newColors.gray,
                                    fontSize: 14,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            {locationsList.map((loc, k) => (
                                <MenuItem value={loc.geoName} key={k}>
                                    {loc.displayLabel}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    {!chart.sensitiveField && (
                    <Tooltip title={_(msg`Download chart`)}>
                        <IconButton
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                backgroundColor: theme.palette.newColors.gray,
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                width: 36,
                                height: 36,
                                "&:hover": {
                                    backgroundColor: theme.palette.newColors.gray,
                                },
                            }}
                        >
                            <SvgCustomIcon name={'download'} color={'white'}/>
                        </IconButton>
                    </Tooltip>
                    )}
                </Box>
            </Box>

            {/* POPUP per download */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{
                    sx: {
                        p: 1,
                        borderRadius: 2,
                        boxShadow: 4,
                        minWidth: 200,
                    },
                }}
            >
                <Box
                    onClick={exportToExcel}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                >
                    <Typography>{_(msg`Download Excel`)}</Typography>
                    <Download sx={{ ml: 1 }} />
                </Box>
                <Box
                    onClick={exportToPdf}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                >
                    <Typography>{_(msg`Download PDF`)}</Typography>
                    <PictureAsPdf sx={{ ml: 1 }} />
                </Box>
            </Popover>

            {/* CHART */}
            <Box
                sx={{
                    position: "relative",
                    ...(fillHeight ? { flex: 1, minHeight: 0, width: "100%" } : { width: "100%" }),
                }}
            >
                {chart.sensitiveField ? (
                    <Box
                        sx={{
                            width: "100%",
                            height: chartHeight,
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            px: 3,
                            textAlign: "center",
                            borderRadius: 1,
                            border: `1px dashed ${theme.palette.divider}`,
                            bgcolor: theme.palette.mode === "light"
                                ? "rgba(0, 0, 0, 0.03)"
                                : "rgba(255, 255, 255, 0.04)",
                        }}
                    >
                        <LockOutlined
                            sx={{
                                fontSize: 40,
                                color: theme.palette.mode === "light"
                                    ? theme.palette.newColors.gray
                                    : theme.palette.newColors.lightGray,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.mode === "light"
                                    ? theme.palette.newColors.gray
                                    : theme.palette.newColors.lightGray,
                                maxWidth: 320,
                            }}
                        >
                            <Trans>This field is marked as sensitive and cannot be used in charts.</Trans>
                        </Typography>
                    </Box>
                ) : (
                <>
                {chart.isLoading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: theme.palette.mode === "light"
                                ? "rgba(255, 255, 255, 0.72)"
                                : "rgba(15, 23, 42, 0.72)",
                            borderRadius: 1,
                        }}
                    >
                        <CircularProgress size={36} />
                    </Box>
                )}
                <ReactECharts
                    key={chart.apiNum ?? chart?.title?.text ?? index}
                    ref={chartRef}
                    style={{ width: "100%", height: chartHeight, opacity: chart.isLoading ? 0.35 : 1 }}
                    option={getChartOption(chart)}
                    notMerge={true}
                />
                </>
                )}
            </Box>
        </Box>

    );
});

export default EChart;
